import { notifyTokensChanged } from './api';

// ── AI call with real-time status updates ────────────────────────

export interface AIStatusEvent {
  type: 'thinking' | 'thinking_delta' | 'searching' | 'analyzing' | 'writing' | 'done';
  message: string;
}

interface AIStreamOptions<T> {
  url: string;
  body: unknown;
  onStatus: (event: AIStatusEvent) => void;
  onResult: (data: T) => void;
  onError: (error: string) => void;
}

/**
 * POST to an AI endpoint with SSE streaming.
 * Receives real-time status events (thinking, searching, writing) and the final result.
 */
export function streamAICall<T>(options: AIStreamOptions<T>): AbortController {
  const controller = new AbortController();
  const token = localStorage.getItem('auth_token');

  fetch(options.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(options.body),
    signal: controller.signal,
  }).then(async (response) => {
    if (!response.ok) {
      try {
        const json = await response.json();
        options.onError(json.error?.message || `HTTP ${response.status}`);
      } catch {
        options.onError(`HTTP ${response.status}`);
      }
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      options.onError('No response stream');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'status') {
              options.onStatus(data as AIStatusEvent);
            } else if (currentEvent === 'result') {
              options.onResult(data.data as T);
              notifyTokensChanged();
            } else if (currentEvent === 'error') {
              options.onError(data.message || 'AI call failed');
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }
    }
  }).catch((err) => {
    if (err.name !== 'AbortError') {
      options.onError(err.message);
    }
  });

  return controller;
}

// ── Batch email generation SSE ───────────────────────────────────

interface SSEOptions<T> {
  url: string;
  body: unknown;
  onBatch: (data: T) => void;
  onDone: (summary: { generated: number; skipped: number; failed: number; total: number }) => void;
  onError: (error: string) => void;
}

export function streamBatchGeneration<T>(options: SSEOptions<T>): AbortController {
  const controller = new AbortController();

  const token = localStorage.getItem('auth_token');

  fetch(options.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(options.body),
    signal: controller.signal,
  }).then(async (response) => {
    if (!response.ok) {
      const json = await response.json();
      options.onError(json.error?.message || `HTTP ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      options.onError('No response stream');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';  // Keep incomplete line in buffer

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const data = JSON.parse(line.slice(6));
          if (currentEvent === 'batch') {
            options.onBatch(data);
          } else if (currentEvent === 'done') {
            options.onDone(data);
            notifyTokensChanged();
          } else if (currentEvent === 'error') {
            options.onError(data.message);
          }
        }
      }
    }
  }).catch((err) => {
    if (err.name !== 'AbortError') {
      options.onError(err.message);
    }
  });

  return controller;
}
